<script>
import { useI18n } from 'vue-i18n';

export default {
	name: 'ProjectModal',
	props: {
		project: {
			type: Object,
			required: true,
		},
		isOpen: {
			type: Boolean,
			required: true,
		},
	},
	emits: ['close'],
	setup() {
		const { t, locale } = useI18n();
		return { t, locale };
	},
	computed: {
		descriptionPoints() {
			const description = this.locale === 'id'
				? this.project.description_id
				: this.project.description_en;

			return Array.isArray(description)
				? description
				: [description || this.project.description];
		},
	},
	methods: {
		closeModal() {
			this.$emit('close');
		},
		handleBackdropClick(event) {
			if (event.target === event.currentTarget) {
				this.closeModal();
			}
		},
	},
};
</script>

<template>
	<!-- Modal Overlay -->
	<transition name="modal">
		<div
			v-if="isOpen"
			class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
			@click="handleBackdropClick"
		>
			<!-- Modal Content -->
			<div class="bg-secondary-light dark:bg-ternary-dark rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
				<!-- Close Button -->
				<div class="sticky top-0 bg-secondary-light dark:bg-ternary-dark border-b border-gray-200 dark:border-secondary-dark p-4 flex justify-between items-center">
					<h1 class="text-2xl font-bold text-ternary-dark dark:text-ternary-light">
						{{ project.title }}
					</h1>
					<button
						@click="closeModal"
						class="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-2xl font-bold transition-colors"
						aria-label="Close modal"
					>
						×
					</button>
				</div>

				<!-- Modal Body -->
				<div class="p-6">
					<!-- Project Image -->
					<div class="mb-8">
						<img
							:src="project.img"
							:alt="project.title"
							class="w-full h-96 object-cover rounded-lg shadow-md"
						>
					</div>

					<!-- Project Link -->
					<div v-if="project.link" class="mb-8 text-center">
						<a
							:href="project.link"
							target="_blank"
							rel="noopener noreferrer"
							class="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
						>
							{{ t('projects.modal.viewProject') }}
						</a>
					</div>

					<!-- Project Description -->
					<div class="mb-8">
						<h2 class="text-xl font-semibold text-ternary-dark dark:text-ternary-light mb-3">
							{{ t('projects.modal.description') }}
						</h2>
						<ul class="list-disc list-inside space-y-2 text-secondary-dark dark:text-gray-300 text-base leading-relaxed text-left">
							<li
								v-for="(point, index) in descriptionPoints"
								:key="index"
							>
								{{ point }}
							</li>
						</ul>
					</div>

					<!-- Project Details Grid -->
					<div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
						<!-- Client Info -->
						<div>
							<h3 class="text-lg font-semibold text-ternary-dark dark:text-ternary-light mb-2">
								{{ t('projects.modal.client') }}
							</h3>
							<p class="text-secondary-dark dark:text-gray-300">
								{{ project.client }}
							</p>
						</div>

						<!-- Date Info -->
						<div>
							<h3 class="text-lg font-semibold text-ternary-dark dark:text-ternary-light mb-2">
								{{ t('projects.modal.date') }}
							</h3>
							<p class="text-secondary-dark dark:text-gray-300">
								{{ project.date }}
							</p>
						</div>

						<!-- Affiliate Info -->
						<div>
							<h3 class="text-lg font-semibold text-ternary-dark dark:text-ternary-light mb-2">
								{{ t('projects.modal.affiliate') }}
							</h3>
							<p class="text-secondary-dark dark:text-gray-300">
								{{ project.affiliate }}
							</p>
						</div>
					</div>

					<!-- Tools and Methodologies -->
					<div class="mb-8">
						<h3 class="text-lg font-semibold text-ternary-dark dark:text-ternary-light mb-3">
							{{ t('projects.modal.tools') }}
						</h3>
						<div class="flex flex-wrap gap-2">
							<span
								v-for="tool in project.tools"
								:key="tool"
								class="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full text-sm font-medium"
							>
								{{ tool }}
							</span>
						</div>
					</div>

					<!-- Tags -->
					<div class="mb-8">
						<h3 class="text-lg font-semibold text-ternary-dark dark:text-ternary-light mb-3">
							{{ t('projects.modal.tags') }}
						</h3>
						<div class="flex flex-wrap gap-2">
							<span
								v-for="tag in project.tags"
								:key="tag"
								class="bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 px-3 py-1 rounded-full text-sm font-medium"
							>
								{{ tag }}
							</span>
						</div>
					</div>
				</div>

				<!-- Modal Footer -->
				<div class="sticky bottom-0 bg-secondary-light dark:bg-ternary-dark border-t border-gray-200 dark:border-secondary-dark p-4 flex justify-end gap-3">
					<button
						@click="closeModal"
						class="bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-ternary-dark dark:text-ternary-light font-medium py-2 px-6 rounded-lg transition-colors duration-200"
					>
						{{ t('projects.modal.close') }}
					</button>
				</div>
			</div>
		</div>
	</transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}

.modal-enter-to,
.modal-leave-from {
	opacity: 1;
}
</style>
